import React from 'react';
import { sampleStatusKey, sampleRowStatusClass, isOutsourcedSample } from '../../utils/sampleStatus';
import './SampleWorklistGrid.css';

const STATUS_LABEL = { pending: 'Pending', collected: 'Collected', received: 'Received', rejected: 'Rejected' };

// Inline stroke icons: render identically on every OS (emoji glyphs do not) and follow currentColor
const ICON_PATHS = {
  collect: 'M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11z',
  receive: 'M4 13h4l2 3h4l2-3h4M5 13l2-8h10l2 8v6H5z',
  label: 'M3 12V4h8l10 10-8 8L3 12zM7.5 7.5h.01',
  reject: 'M6 6l12 12M18 6L6 18',
  log: 'M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01',
  lab: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z',
  print: 'M7 9V3h10v6M7 17H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2M7 14h10v7H7z',
  done: 'M5 12.5l4.5 4.5L19 7.5',
};
const Icon = ({ name }) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d={ICON_PATHS[name]} />
  </svg>
);

/**
 * One action slot. Every row renders the same slots in the same order; a slot that does not
 * apply is frozen (disabled), never removed, so buttons line up in every row.
 *   state: 'ready' | 'done' (completed: muted + ✓) | 'na' (not applicable for this status)
 */
const ActionSlot = ({ action }) => {
  const { label, icon, state, tone, size = 'md', title, onClick } = action;
  const disabled = state !== 'ready';
  return (
    <button
      type="button"
      className={`swg-act swg-act-${size} swg-act-${tone || 'neutral'} swg-act-${state} swg-act-${action.key}`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
      aria-label={`${label}${state === 'done' ? ' (completed)' : state === 'na' ? ' (not available)' : ''}`}
    >
      <span className="swg-act-icon"><Icon name={state === 'done' ? 'done' : icon} /></span>
      <span className="swg-act-label">{label}</span>
    </button>
  );
};

/**
 * Shared worklist grid for Sample Collection and Sample Receive.
 * Presentation only: eligibility, actions and handlers come from the page.
 */
const SampleWorklistGrid = ({
  samples,
  selectedIds,
  isSelectable,
  onToggleSelect,
  allSelected,
  onToggleAll,
  selectAllTitle,
  getContainerInfo,
  onPrintBarcode,
  getActions,
  actionSlots = { md: 3, sm: 1 },
}) => (
  <div className="swg-wrap">
    <table
      className="swg-table"
      style={{ "--swg-md": actionSlots.md, "--swg-sm": actionSlots.sm, "--swg-gaps": actionSlots.md + actionSlots.sm - 1 }}
    >
      <thead>
        <tr>
          <th className="swg-th-check">
            <input type="checkbox" checked={allSelected} onChange={onToggleAll} title={selectAllTitle} aria-label={selectAllTitle} />
          </th>
          <th>Patient</th>
          <th>Investigation</th>
          <th className="swg-th-dept">Department</th>
          <th>Specimen</th>
          <th>Barcode / SIN</th>
          <th>Status</th>
          <th className="swg-th-actions">Actions</th>
        </tr>
      </thead>
      <tbody>
        {samples.map((sample) => {
          const statusKey = sampleStatusKey(sample);
          const selectable = isSelectable(sample);
          const selected = selectedIds.has(sample.testId);
          const tube = getContainerInfo(sample.containerName, sample.containerColor);
          const outsourced = isOutsourcedSample(sample);
          const urgent = sample.priority === 'Urgent' || sample.isUrgent;
          return (
            <tr
              key={sample.testId}
              className={`swg-row ${sampleRowStatusClass(sample)} ${selected ? 'is-selected' : ''}`}
            >
              <td className="swg-td-check">
                <input
                  type="checkbox"
                  checked={selected}
                  disabled={!selectable}
                  onChange={() => onToggleSelect(sample)}
                  aria-label={`Select ${sample.itemName} for ${sample.patientName}`}
                />
              </td>

              <td className="swg-td-patient">
                <div className="swg-line">
                  <span className="swg-pname" title={sample.patientName}>{sample.patientName}</span>
                  {urgent && <span className="swg-chip swg-chip-stat">STAT</span>}
                  <span className="swg-muted">{sample.ageGender || '—'}</span>
                </div>
                <div className="swg-line swg-ids">
                  <span title="UHID / Patient ID">UHID <b>{sample.patientId}</b></span>
                  <span title="Lab No">LAB <b>{sample.ledgerTransactionNo}</b></span>
                </div>
              </td>

              <td className="swg-td-test">
                <div className="swg-test" title={sample.itemName}>{sample.itemName}</div>
                {outsourced && (
                  <div className="swg-outsource-note" title={sample.referenceLabName || 'Reference lab'}>
                    Outsourced{sample.referenceLabName ? ` · ${sample.referenceLabName}` : ''}
                  </div>
                )}
              </td>

              <td className="swg-td-dept">
                <span className="swg-dept" title={sample.departmentName || 'General'}>
                  {sample.departmentName || 'General'}
                </span>
              </td>

              <td className="swg-td-specimen">
                <div className="swg-line" title={sample.containerName || tube.label}>
                  <span className="swg-tube" style={{ background: tube.dotColor, borderColor: tube.border }} aria-hidden="true" />
                  <span className="swg-specimen">{sample.sampleTypeName || 'Serum'}</span>
                </div>
              </td>

              <td className="swg-td-barcode">
                {sample.barcodeNo ? (
                  <div className="swg-barcode" role="group" aria-label={`Barcode ${sample.barcodeNo}`}>
                    <span className="swg-barcode-no" title={sample.barcodeNo}>{sample.barcodeNo}</span>
                    <button
                      type="button"
                      className="swg-barcode-print"
                      onClick={() => onPrintBarcode(sample)}
                      title="Print thermal barcode label"
                      aria-label={`Print label ${sample.barcodeNo}`}
                    >
                      <Icon name="print" />
                    </button>
                  </div>
                ) : (
                  <span className="swg-muted">Not allocated</span>
                )}
              </td>

              <td className="swg-td-status">
                <span className={`swg-status swg-status-${statusKey}`}>{STATUS_LABEL[statusKey]}</span>
              </td>

              <td className="swg-td-actions">
                <div className="swg-actions">
                  {getActions(sample).map((a) => (
                    <ActionSlot key={a.key} action={a} />
                  ))}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default SampleWorklistGrid;
